import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";

const isFilled = (value) => String(value ?? "").trim().length > 0;

const CourseOverviewSection = ({ courseData, onChange }) => {
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [prerequisite, setPrerequisite] = useState("");
  const [targetAudience, setTargetAudience] = useState("");

  useEffect(() => {
    if (!courseData) return;
    const overview = courseData?.courseOverview || {};
    setDescription(overview.courseDescription || "");
    setDuration(overview.courseDuration || "");
    setPrerequisite(overview.coursePrerequisite || "");
    setTargetAudience(overview.courseTargetAudience || "");
  }, [courseData?._id]);

  const emitChange = (changes = {}) => {
    const nextDescription = Object.prototype.hasOwnProperty.call(
      changes,
      "description",
    )
      ? changes.description
      : description;

    const nextDuration = Object.prototype.hasOwnProperty.call(
      changes,
      "duration",
    )
      ? changes.duration
      : duration;

    const nextPrerequisite = Object.prototype.hasOwnProperty.call(
      changes,
      "prerequisite",
    )
      ? changes.prerequisite
      : prerequisite;

    const nextTargetAudience = Object.prototype.hasOwnProperty.call(
      changes,
      "targetAudience",
    )
      ? changes.targetAudience
      : targetAudience;

    onChange?.({
      courseDescription: nextDescription,
      courseDuration: nextDuration,
      coursePrerequisite: nextPrerequisite,
      courseTargetAudience: nextTargetAudience,
    });
  };

  const fields = {
    description: isFilled(description),
    duration: isFilled(duration),
    prerequisite: isFilled(prerequisite),
    targetAudience: isFilled(targetAudience),
  };

  const completedCount = Object.values(fields).filter(Boolean).length;
  const isComplete = completedCount === 4;

  const FieldLabel = ({ children, complete }) => (
    <p className="mb-1 flex items-center gap-1 text-sm text-[#374151]">
      {children}
      {complete && <Check size={14} className="text-[#16A34A]" />}
    </p>
  );

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_#0000000D]">
      <header className="flex flex-col gap-3 border-b border-[#E5E7EB] bg-[#F9FAFB] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex items-center gap-3">
          <h3 className="text-base text-[#1F2937] sm:text-lg">
            Course Overview
          </h3>

          <span
            className={`rounded-full px-2.5 py-0.5 text-xs ${
              isComplete
                ? "bg-[#DCFCE7] text-[#166534]"
                : "bg-[#FEF3C7] text-[#92400E]"
            }`}
          >
            {isComplete ? "Complete" : "Incomplete"}
          </span>
        </div>

        <p className="text-sm text-[#6B7280]">
          {completedCount}/4 fields completed
        </p>
      </header>

      <div className="px-4 pb-8 pt-6 sm:px-6 md:pb-12">
        <div>
          <FieldLabel complete={fields.description}>
            Course Description
          </FieldLabel>

          <textarea
            value={description}
            onChange={(e) => {
              const value = e.target.value;
              setDescription(value);
              emitChange({ description: value });
            }}
            placeholder="Enter course description"
            className="min-h-[89px] w-full resize-none rounded-lg border border-[#D1D5DB] px-3 py-2 text-sm text-black outline-none sm:text-base"
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <FieldLabel complete={fields.duration}>Duration</FieldLabel>

            <input
              type="text"
              value={duration}
              onChange={(e) => {
                const value = e.target.value;
                setDuration(value);
                emitChange({ duration: value });
              }}
              placeholder="e.g. 3 months"
              className="h-[42px] w-full rounded-lg border border-[#D1D5DB] bg-white px-3 text-sm text-black outline-none sm:text-base"
            />
          </div>

          {/* Prerequisites */}
          <div>
            <FieldLabel complete={fields.prerequisite}>
              Prerequisites
            </FieldLabel>

            <input
              value={prerequisite}
              onChange={(e) => {
                const value = e.target.value;
                setPrerequisite(value);
                emitChange({ prerequisite: value });
              }}
              placeholder="Enter prerequisites"
              className="h-[42px] w-full rounded-lg border border-[#D1D5DB] px-3 text-sm text-black outline-none sm:text-base"
            />
          </div>

          {/* Target Audience */}
          <div>
            <FieldLabel complete={fields.targetAudience}>
              Target Audience
            </FieldLabel>

            <input
              value={targetAudience}
              onChange={(e) => {
                const value = e.target.value;
                setTargetAudience(value);
                emitChange({ targetAudience: value });
              }}
              placeholder="Enter target audience"
              className="h-[42px] w-full rounded-lg border border-[#D1D5DB] px-3 text-sm text-black outline-none sm:text-base"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseOverviewSection;
